import { useCallback, useState } from 'react'

import {
  GenerateTrainingsScormPackagePreviewLinkDocument,
  GenerateTrainingsScormPackagePreviewLinkMutation,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface UseScormPackagePreviewLinkParams {
  scormPackageId: string | undefined
}

export const useScormPackagePreviewLink = ({
  scormPackageId,
}: UseScormPackagePreviewLinkParams) => {
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [iframeLoaded, setIframeLoaded] = useState(false)

  const [generatePreviewLink, { loading }] = useMutation(
    GenerateTrainingsScormPackagePreviewLinkDocument,
    {
      extractorFn: (data: GenerateTrainingsScormPackagePreviewLinkMutation) =>
        data.trainingsMutations.generatePreviewLinkTrainingsScormPackage,
    }
  )

  const openPreview = useCallback(async () => {
    if (!scormPackageId) {
      setError(i18n.t('trainings.scorm.preview.error'))
      return
    }

    setError(null)
    setIframeLoaded(false)

    const result = await generatePreviewLink({
      variables: {
        id: scormPackageId,
        redirectUrl: window.location.origin + window.location.pathname,
      },
    })

    const handled = handleResult(result, () => {
      setError(i18n.t('trainings.scorm.preview.error'))
    })

    if (handled.success) {
      const url = handled.data.scormPackage?.previewUrl
      if (url) {
        setPreviewUrl(url)
      } else {
        setError(i18n.t('trainings.scorm.preview.error'))
      }
    }
  }, [generatePreviewLink, scormPackageId])

  const closePreview = useCallback(() => setPreviewUrl(null), [])

  const handleIframeLoad = useCallback(
    (event: React.SyntheticEvent<HTMLIFrameElement>) => {
      try {
        const href = event.currentTarget.contentWindow?.location.href
        // Close the preview only when the iframe has navigated away from the SCORM Cloud
        // player URL (i.e. the redirect back to this app has completed). We exclude
        // 'about:blank' (initial load in some browsers) and the preview URL itself
        // (still on the player) to avoid closing the dialog prematurely.
        if (href && href !== 'about:blank' && href !== previewUrl) closePreview()
        else setIframeLoaded(true)
      } catch {
        // Still on SCORM Cloud domain (cross-origin), course is still running, ignore
        setIframeLoaded(true)
      }
    },
    [closePreview, previewUrl]
  )

  return { openPreview, closePreview, handleIframeLoad, previewUrl, iframeLoaded, loading, error }
}
